import { ChangeDetectionStrategy, Component, computed, inject, ViewEncapsulation } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { AttendanceService } from "./attendance.service";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";

/** Parses `#!/guest-registration-confirm/<token>`. */
export function parseGuestRegistrationConfirmToken(hash: string): string | undefined {
    const match = hash.match(/guest-registration-confirm\/([^/?&#]+)/);
    return match?.[1];
}

@TypeManagerDecorator("s25-ng-guest-registration-confirm")
@Component({
    standalone: true,
    selector: "s25-ng-guest-registration-confirm",
    template: `
        <div class="c-padding--double">
            @if (!token()) {
                <div class="message-panel message-panel--error">
                    <h2>Invalid confirmation link</h2>
                    <p>This registration confirmation link is missing or malformed.</p>
                </div>
            } @else if (confirmResult.resource.isLoading()) {
                <s25-ng-loading-inline-static [text]="'Confirming your registration...'" />
            } @else if (confirmResult.resource.error()) {
                <div class="message-panel message-panel--error">
                    <h2>Registration could not be confirmed</h2>
                    <p>Registration could not be confirmed. Please try again.</p>
                </div>
            } @else {
                <div class="message-panel message-panel--success">
                    <h2>Registration confirmed</h2>
                    <p>You are registered for this event.</p>
                </div>
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        .message-panel {
            max-width: 36rem;
            margin: 0 auto;
            text-align: center;
            padding: 3rem 1rem;
        }

        .message-panel h2 {
            margin-bottom: 1rem;
            color: #2573a7;
        }

        .message-panel--success h2 {
            color: #2e7d32;
        }

        .message-panel--error h2 {
            color: #d32f2f;
        }

        .message-panel p {
            margin: 0;
            color: #555;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25LoadingInlineStaticComponent],
})
export class SeGuestRegistrationConfirmComponent {
    private readonly attendanceService = inject(AttendanceService);

    readonly token = computed(() => parseGuestRegistrationConfirmToken(window.location.hash));

    readonly confirmResult = s25Resource({
        if: () => !!this.token(),
        params: () => this.token(),
        loader: async ({ params: confirmToken, abortSignal }) => {
            return this.attendanceService.confirmGuest(confirmToken, { abortSignal });
        },
    });
}
